import { Building2, ChevronRight, Menu, PhoneCall, Pill, Sparkles, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '../../common/Button';
import Container from '../../common/Container';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isPharmacy = location.pathname.startsWith('/pharmacy');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string, targetPath?: string) => {
    setMobileMenuOpen(false);
    const target = targetPath || (isPharmacy ? '/pharmacy' : '/');

    if (location.pathname !== target) {
      navigate(target);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const yOffset = -80;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
      return;
    }

    const element = document.getElementById(id);
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200/80 py-3'
          : 'bg-white/80 backdrop-blur-sm py-4'
      }`}
    >
      <Container>
        <div className="flex items-center justify-between">
          {/* Brand Logo */}
          <Link
            to={isPharmacy ? '/pharmacy' : '/'}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-3 group focus:outline-none"
            aria-label={isPharmacy ? 'Divine Pharmacy Home' : 'Divine HMS Home'}
          >
            <div className="relative flex items-center justify-center p-1.5 rounded-xl bg-gradient-to-br from-[#0D7893]/10 to-transparent border border-[#0D7893]/20 group-hover:border-[#0D7893]/40 transition-all">
              <img
                src="/img/svg/hms_logo.png"
                alt="HMS Logo"
                className="h-10 w-auto object-contain transition-transform group-hover:scale-105"
              />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="text-xl font-bold tracking-tight text-slate-900 group-hover:text-[#0D7893] transition-colors">
                  Divine <span className="text-[#0D7893]">{isPharmacy ? 'Pharmacy' : 'HMS'}</span>
                </span>
                <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-[#0D7893]/10 text-[#0D7893] border border-[#0D7893]/20">
                  <Sparkles className="w-2.5 h-2.5" /> {isPharmacy ? 'Pharma ERP' : 'Hospital ERP'}
                </span>
              </div>
              <p className="hidden md:block text-[11px] text-slate-500 font-medium -mt-0.5">
                {isPharmacy ? 'Medical & Retail ERP' : 'Hospital Information System'}
              </p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
            <button
              onClick={() => scrollTo('about')}
              className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-[#0D7893] hover:bg-[#0D7893]/5 rounded-lg transition-colors"
            >
              Overview
            </button>
            <button
              onClick={() => scrollTo('modules')}
              className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-[#0D7893] hover:bg-[#0D7893]/5 rounded-lg transition-colors"
            >
              {isPharmacy ? 'Pharmacy Modules' : 'Core Modules'}
            </button>
            <button
              onClick={() => scrollTo('features')}
              className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-[#0D7893] hover:bg-[#0D7893]/5 rounded-lg transition-colors"
            >
              Features
            </button>
            <button
              onClick={() => scrollTo('reports')}
              className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-[#0D7893] hover:bg-[#0D7893]/5 rounded-lg transition-colors"
            >
              Analytics
            </button>
            <button
              onClick={() => scrollTo('contact')}
              className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-[#0D7893] hover:bg-[#0D7893]/5 rounded-lg transition-colors"
            >
              Contact
            </button>

            {/* Cross-Product Switcher */}
            <div className="ml-2 pl-2 border-l border-slate-200">
              {isPharmacy ? (
                <Link
                  to="/"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0D7893]/10 hover:bg-[#0D7893]/20 text-[#0D7893] text-xs font-semibold border border-[#0D7893]/20 transition-all"
                  title="Switch to Hospital Management System"
                >
                  <Building2 className="w-3.5 h-3.5" />
                  <span>Hospital ERP</span>
                </Link>
              ) : (
                <Link
                  to="/pharmacy"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0D7893]/10 hover:bg-[#0D7893]/20 text-[#0D7893] text-xs font-semibold border border-[#0D7893]/20 transition-all"
                  title="Switch to Pharmacy Management System"
                >
                  <Pill className="w-3.5 h-3.5" />
                  <span>Pharmacy ERP</span>
                </Link>
              )}
            </div>
          </nav>

          {/* Right Action buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:9752002111"
              className="flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-[#0D7893] px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <PhoneCall className="w-3.5 h-3.5 text-[#0D7893]" />
              <span>+91 9752002111</span>
            </a>
            <Button
              size="sm"
              onClick={() => scrollTo('contact')}
              className="flex items-center gap-2 shadow-sm font-semibold tracking-wide"
            >
              <span>Request Demo</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center lg:hidden gap-2">
            <Button size="sm" onClick={() => scrollTo('contact')} className="text-xs px-3 py-2">
              Demo
            </Button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-xl text-slate-700 hover:text-[#0D7893] hover:bg-slate-100 focus:outline-none transition-colors"
              aria-label="Toggle Navigation Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </Container>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 top-[65px] z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Drawer content */}
          <div className="relative bg-white border-b border-slate-200 px-6 py-6 shadow-2xl space-y-4 animate-in slide-in-from-top duration-200">
            {/* System Switcher Tab for Mobile */}
            <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-xl">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${
                  !isPharmacy
                    ? 'bg-white text-[#0D7893] shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Building2 className="w-3.5 h-3.5" />
                <span>Hospital ERP</span>
              </Link>
              <Link
                to="/pharmacy"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${
                  isPharmacy
                    ? 'bg-white text-[#0D7893] shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Pill className="w-3.5 h-3.5" />
                <span>Pharmacy ERP</span>
              </Link>
            </div>

            <div className="flex flex-col space-y-2">
              <button
                onClick={() => scrollTo('about')}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-base font-medium text-slate-800 hover:bg-[#0D7893]/10 hover:text-[#0D7893] text-left transition-colors"
              >
                <span>Overview</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
              <button
                onClick={() => scrollTo('modules')}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-base font-medium text-slate-800 hover:bg-[#0D7893]/10 hover:text-[#0D7893] text-left transition-colors"
              >
                <span>{isPharmacy ? 'Pharmacy Modules' : 'Core Hospital Modules'}</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
              <button
                onClick={() => scrollTo('features')}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-base font-medium text-slate-800 hover:bg-[#0D7893]/10 hover:text-[#0D7893] text-left transition-colors"
              >
                <span>{isPharmacy ? 'Why Divine Pharmacy' : 'Why Divine HMS'}</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
              <button
                onClick={() => scrollTo('reports')}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-base font-medium text-slate-800 hover:bg-[#0D7893]/10 hover:text-[#0D7893] text-left transition-colors"
              >
                <span>Reports & Analytics</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
              <button
                onClick={() => scrollTo('contact')}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-base font-medium text-slate-800 hover:bg-[#0D7893]/10 hover:text-[#0D7893] text-left transition-colors"
              >
                <span>Contact & Sales</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            <div className="pt-4 border-t border-slate-100 flex flex-col gap-3">
              <a
                href="tel:9752002111"
                className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
              >
                <PhoneCall className="w-4 h-4 text-[#0D7893]" />
                <span>Call +91 9752002111</span>
              </a>
              <Button
                size="md"
                onClick={() => scrollTo('contact')}
                className="w-full flex items-center justify-center gap-2"
              >
                <span>Book a Guided Demo</span>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
